import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Grid,
  GridItem,
  Text,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { t } from '@/lib/helpers'
import type { SubmitHandler } from 'react-hook-form'
import type { FilterParams } from '@/features/filters/types'

export interface ToshiOsAddPointsLogFilterType {
  q?: FilterParams['q']
  status_code?: FilterParams['status_code']
  from_date?: FilterParams['from_date']
  to_date?: FilterParams['to_date']
}

export interface ToshiOsAddPointsLogFilterFormProps {
  onSearch?: (filter: ToshiOsAddPointsLogFilterType) => void
}

const schema = yup.object().shape({
  q: yup
    .string()
    .nullable()
    .transform((v) => (v === '' ? undefined : v)),
  status_code: yup
    .mixed<'all' | '400'| '401'| '403'| '404'| '429'| '500'| '501'| '503'>()
    .oneOf(['all', '400', '401', '403', '404', '429', '500', '501', '503']),
  from_date: yup.string().nullable(),
  to_date: yup
    .string()
    .nullable()
    .test('after-from', t('views.admin.shared.date_range_error'), function (value) {
      const { from_date } = this.parent
      if (!value || !from_date) return true
      return new Date(value) >= new Date(from_date)
    }),
})

export const ToshiOsAddPointsLogFilterForm = ({ onSearch }: ToshiOsAddPointsLogFilterFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ToshiOsAddPointsLogFilterType>({
    defaultValues: {
      q: undefined,
      status_code: 'all',
      from_date: '',
      to_date: '',
    },
    resolver: yupResolver(schema),
  })

  const onSubmit: SubmitHandler<ToshiOsAddPointsLogFilterType> = (data) => {
    if (isSubmitting) return
    onSearch && onSearch(data)
  }

  const STATUS_CODE_OPTIONS = [
    { value: '400', label: '400 Bad Request' },
    { value: '401', label: '401 Unauthorized' },
    { value: '403', label: '403 Forbidden' },
    { value: '404', label: '404 Not Found' },
    { value: '429', label: '429 Too Many Requests' },
    { value: '500', label: '500 Internal Server Error' },
    { value: '501', label: '501 Not Implemented' },
    { value: '503', label: '503 Service Unavailable' },
  ];

  return (
    <Box
      sx={{
        paddingRight: '40px',
        paddingLeft: '40px',
        marginTop: '-9px',
        marginBottom: '24px',
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid gap={4} templateColumns="repeat(12, 1fr)">
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="0.875rem" fontWeight="400">
                {t('views.admin.toshi_os_add_points_logs.filters.keyword')}
              </FormLabel>
              <Controller
                control={control}
                defaultValue=""
                name="q"
                render={(field) => <Input {...field} />}
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={2}>
            <FormControl>
              <FormLabel fontSize="0.875rem" fontWeight="400">
                {t('views.admin.toshi_os_add_points_logs.filters.from_date')}
              </FormLabel>
              <Controller
                control={control}
                name="from_date"
                render={(field) => <Input type="date" {...field} />}
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={2}>
            <FormControl>
              <FormLabel fontSize="0.875rem" fontWeight="400">
                {t('views.admin.toshi_os_add_points_logs.filters.to_date')}
              </FormLabel>
              <Controller
                control={control}
                name="to_date"
                render={(field) => <Input type="date" {...field} />}
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={2}>
            <FormControl>
              <FormLabel fontSize="0.875rem" fontWeight="400">
                {t('views.admin.toshi_os_add_points_logs.filters.status_code')}
              </FormLabel>
              <Controller
                control={control}
                name="status_code"
                render={(field) => (
                  <Select {...field}>
                    <option value="all">
                      {t('views.admin.toshi_os_add_points_logs.filters.all')}
                    </option>
                    {STATUS_CODE_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={2}>
            <FormControl>
              <FormLabel
                fontSize="0.875rem"
                fontWeight="400"
                sx={{ visibility: 'hidden' }}
              >
                {t('views.admin.toshi_os_add_points_logs.filters.search')}
              </FormLabel>
              <Button isFullWidth type="submit">
                {t('views.admin.toshi_os_add_points_logs.filters.search')}
              </Button>
            </FormControl>
          </GridItem>
        </Grid>

        {errors.to_date && (
          <Text color="red.600" fontSize="xs">
            {errors.to_date.message}
          </Text>
        )}
      </form>
    </Box>
  )
}
